/**
 * Identity-hygiene — TanStack Query hooks.
 *
 * The dormant-access list is the primary read; the rebuild
 * mutation is the operator-side button to backfill from the
 * audit log.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import {
  campaignListSchema,
  campaignItemListSchema,
  dormantListSchema,
  hygieneSummarySchema,
  policySchema,
  revokeLogListSchema,
  type Campaign,
  type CampaignItem,
  type DormantFilterSearch,
  type DormantList,
  type HygieneSummary,
  type ItemDecision,
  type Policy,
  type RevokeLogList,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity/hygiene`;
}

const PAGE_SIZE = 50;

function buildQueryString(filter: DormantFilterSearch): string {
  const params = new URLSearchParams();
  if (filter.tone) params.set("tone", filter.tone);
  if (filter.grant_source) params.set("grant_source", filter.grant_source);
  if (filter.application_id) params.set("application_id", filter.application_id);
  if (filter.user_id) params.set("user_id", filter.user_id);
  if (filter.min_days_idle != null) {
    params.set("min_days_idle", String(filter.min_days_idle));
  }
  if (filter.include_fresh) params.set("include_fresh", "true");
  const page = filter.page ?? 1;
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String((page - 1) * PAGE_SIZE));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchDormantList(
  tenantID: string,
  filter: DormantFilterSearch,
): Promise<DormantList> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/dormant${buildQueryString(filter)}`);
  return dormantListSchema.parse(raw);
}

export function useDormantListQuery(
  tenantID: string,
  filter: DormantFilterSearch,
  enabled = true,
) {
  return useQuery<DormantList, ApiError>({
    queryKey: ["identity-hygiene", "dormant", tenantID, filter],
    queryFn: () => fetchDormantList(tenantID, filter),
    enabled: enabled && tenantID.length > 0,
    // Hygiene moves slowly — refresh on focus + a 5-minute
    // background interval.
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export async function fetchHygieneSummary(tenantID: string): Promise<HygieneSummary> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/summary`);
  return hygieneSummarySchema.parse(raw);
}

export function useHygieneSummaryQuery(tenantID: string) {
  return useQuery<HygieneSummary, ApiError>({
    queryKey: ["identity-hygiene", "summary", tenantID],
    queryFn: () => fetchHygieneSummary(tenantID),
    enabled: tenantID.length > 0,
    staleTime: 60_000,
  });
}

export interface RebuildResult {
  rows_affected: number;
  lookback_days: number;
}

export function useRebuildHygieneMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<RebuildResult, ApiError, { lookbackDays?: number } | void>({
    mutationFn: async (input) => {
      const lookback = (input && (input as { lookbackDays?: number }).lookbackDays) ?? 365;
      return withStepUp(() =>
        apiFetch<RebuildResult>(`${base(tenantID)}/rebuild?lookback_days=${lookback}`, {
          method: "POST",
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "dormant", tenantID] });
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "summary", tenantID] });
    },
  });
}

// ── Policy ──────────────────────────────────────────────────────────────

export function usePolicyQuery(tenantID: string) {
  return useQuery<Policy, ApiError>({
    queryKey: ["identity-hygiene", "policy", tenantID],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`${base(tenantID)}/policy`);
      return policySchema.parse(raw);
    },
    enabled: tenantID.length > 0,
    staleTime: 30_000,
  });
}

export function usePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Policy, ApiError, Policy & { approved_by?: string }>({
    mutationFn: async (input) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/policy`, {
          method: "PUT",
          body: input,
        }),
      );
      return policySchema.parse(raw);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "policy", tenantID] });
    },
  });
}

// ── Revoke ──────────────────────────────────────────────────────────────

export function useRevokeMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiError,
    { user_id: string; application_id: string; grant_source: "direct" | "both" }
  >({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/revoke`, {
          method: "POST",
          body: input,
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "dormant", tenantID] });
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "revocations", tenantID] });
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "summary", tenantID] });
    },
  });
}

export function useRevokeLogQuery(tenantID: string, limit = 100) {
  return useQuery<RevokeLogList, ApiError>({
    queryKey: ["identity-hygiene", "revocations", tenantID, limit],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/revocations?limit=${limit}`,
      );
      return revokeLogListSchema.parse(raw);
    },
    enabled: tenantID.length > 0,
    staleTime: 60_000,
  });
}

// ── Campaigns ───────────────────────────────────────────────────────────

export function useCampaignsQuery(tenantID: string) {
  return useQuery<{ data: Campaign[] }, ApiError>({
    queryKey: ["identity-hygiene", "campaigns", tenantID],
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`${base(tenantID)}/campaigns`);
      return campaignListSchema.parse(raw);
    },
    enabled: tenantID.length > 0,
    staleTime: 30_000,
  });
}

export function useCampaignItemsQuery(
  tenantID: string,
  campaignID: string,
  opts?: { mine?: boolean; decision?: ItemDecision },
) {
  return useQuery<{ data: CampaignItem[] }, ApiError>({
    queryKey: ["identity-hygiene", "campaign-items", tenantID, campaignID, opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.mine) params.set("mine", "true");
      if (opts?.decision) params.set("decision", opts.decision);
      const qs = params.toString();
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/campaigns/${encodeURIComponent(campaignID)}/items${qs ? `?${qs}` : ""}`,
      );
      return campaignItemListSchema.parse(raw);
    },
    enabled: tenantID.length > 0 && campaignID.length > 0,
    staleTime: 30_000,
  });
}

export function useCreateCampaignMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Campaign, ApiError, {
    name: string;
    description?: string;
    scope: { min_days_idle?: number; tone?: string };
    opens_at: string;
    closes_at: string;
    reviewer_admin_id?: string;
  }>({
    mutationFn: async (input) => {
      const raw = await withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/campaigns`, {
          method: "POST",
          body: input,
        }),
      );
      // Reuse the campaign list element shape — same fields.
      const parsed = campaignListSchema.parse({ data: [raw] });
      return parsed.data[0]!;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
    },
  });
}

export function useOpenCampaignMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/campaigns/${encodeURIComponent(id)}/open`, {
          method: "POST",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
    },
  });
}

export function useCloseCampaignMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/campaigns/${encodeURIComponent(id)}/close`, {
          method: "POST",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "dormant", tenantID] });
    },
  });
}

export function useDecideItemMutation(tenantID: string, campaignID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { itemID: string; decision: ItemDecision; comment?: string }>({
    mutationFn: ({ itemID, decision, comment }) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/items/${encodeURIComponent(itemID)}/decide`, {
          method: "POST",
          body: { decision, comment: comment ?? "" },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["identity-hygiene", "campaign-items", tenantID, campaignID],
      });
      qc.invalidateQueries({ queryKey: ["identity-hygiene", "campaigns", tenantID] });
    },
  });
}
